import React, { FC, useContext, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { FlowHeader, SpinnerView } from "@app/components";
import {
  DrawRequestContext,
  KitContext,
  OrderRenewalContext,
  ProfileContext,
} from "@app/provider";
import { TestType } from "@app/provider/testData/types";
import { ResultCodes } from "@app/service/resultCodes";
import { useDialog } from "@natera/platform/lib/hooks";
import DrawRequestDialog from "@app/components/drawRequestDialog";
import { TestDetailsContext } from "@app/provider/testData";
import { SampleDrawKitStep } from "@app/components/sampleDraw/steps";

import MobilePhlebotomyStepper from "./mobilePhlebotomyStepper";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "@app/routing";
import { ComboOrderModal } from "@app/components/sampleDraw/dialogs";
import TimingInfoDialog from "@app/components/sampleDraw/steps/kit/timingInfoDialog";
import { getReadableComboOrderTestTypesList } from "@app/utils";
import { TestDetailsRecord } from "@app/provider/types";
import { DrawRequestFlow } from "@app/provider/drawRequest";
import OrderInWrongStateScreen from "@app/pages/public/scheduleBloodDraw/orderInWrongState/OrderInWrongStateScreen";

import "./sampleDraw.scss";

const messages = defineMessages({
  sampleDrawLabTitle: {
    id: "sampleDrawLabTitle",
    defaultMessage: "Lab Locator",
  },
  sampleDrawCannotBeRequestedTitle: {
    id: "sampleDrawCannotBeRequestedTitle",
    defaultMessage: "We're sorry.",
  },
  sampleDrawCannotBeRequestedDesc: {
    id: "sampleDrawCannotBeRequestedDesc",
    defaultMessage: "Mobile phlebotomy cannot be requested for this test",
  },
  sampleDrawNotFound: {
    id: "sampleDrawNotFound",
    defaultMessage: "Test not found",
  },
});

interface LocationState {
  fromSimpleOrderFlow?: boolean;
  fromTokenizedLink?: boolean;
}

const CreateMobilePhlebotomy: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;

  const {
    checkSampleDraw,
    isLoading: DrawRequestLoading,
    comboOrders,
  } = useContext(DrawRequestContext);
  const { getRenewalInfo, isLoading: OrderRenewalLoading } =
    React.useContext(OrderRenewalContext);
  const { setSkipKitShipping, setSkipLabStep } = React.useContext(KitContext);
  const { profileData } = useContext(ProfileContext);

  const drawRequestDialog = useDialog(DrawRequestDialog);
  const comboOrderModal = useDialog(ComboOrderModal);
  const timingDialog = useDialog(TimingInfoDialog);
  const { getTestDetails, getOrderUidPublic, getTestUid } =
    useContext(TestDetailsContext);

  const [kitShippingNeeded, setKitShippingNeeded] = useState<boolean>(false);
  const [isPossibleToCreate, setIsPossibleToCreate] = useState(false);
  const [isProsperaEbddPassed, setIsProsperaEbddPassed] = useState(false);

  const [isKitShippingStep, setIsKitShippingStep] = useState(false);

  const [testDetailsRecord, setTestDetailsRecord] =
    useState<TestDetailsRecord>();
  const [orderUidPublic, setOrderUidPublic] = useState<string>();
  const [testUid, setTestUid] = useState<string>();

  const testType = testDetailsRecord?.testType;
  const isOncologyOrder = testDetailsRecord?.isOncologyOrder;

  useEffect(() => {
    setTestDetailsRecord(getTestDetails());
    setOrderUidPublic(getOrderUidPublic());
    setTestUid(getTestUid());
  }, []);

  useEffect(() => {
    if (isOncologyOrder) {
      const closeTimingDialogAndRedirect = () => {
        timingDialog.close();
        navigate(routes.home);
      };

      timingDialog.open({
        onClose: closeTimingDialogAndRedirect,
        onContinue: timingDialog.close,
      });
    }
  }, [isOncologyOrder]);

  useEffect(() => {
    if (comboOrders.length > 1) {
      const testTypes = comboOrders.map((order) => order.testType);
      const orderTypes = getReadableComboOrderTestTypesList(testTypes);
      comboOrderModal.open({ orderTypes, onClose: comboOrderModal.close });
    }
  }, [comboOrders.length]);

  const handleKitShippingStepPassed = async () => {
    setIsKitShippingStep(false);
  };

  const handleBackKitStep = async () => {
    const isFromTokenizedLink = locationState?.fromTokenizedLink;

    if (isFromTokenizedLink) {
      navigate(routes.home);
    } else {
      window.history.length > 1 ? navigate(-1) : navigate(routes.home);
    }
  };
  const handleBackPreferenceStep = () => {
    if (locationState?.fromSimpleOrderFlow) {
      navigate(routes.home);
    } else if (window.history.length <= 1) {
      navigate(routes.home);
    } else {
      navigate(-1);
    }
  };

  const handleBackAppointmentTimeStep = () => {
    navigate(routes.home, { replace: true });
  };

  const handleBackKitShippingStep = async () => {
    if (!kitShippingNeeded) {
      navigate(routes.home);
    }
    setIsKitShippingStep(kitShippingNeeded);
  };

  const handleCheckSample = async (orderUidPublic: string, testUid: string) => {
    try {
      const response = await checkSampleDraw(orderUidPublic, testUid);
      if (response?.isValid) {
        setKitShippingNeeded(response.needsKitShippingStep);
        setSkipLabStep(response.skipLabStep);
        setIsPossibleToCreate(true);
      }
      setSkipKitShipping(!response?.needsKitShippingStep);
    } catch (error) {
      switch (error.code) {
        case ResultCodes.TEST_NOT_FOUND:
          drawRequestDialog.open({
            title: intl.formatMessage(messages.sampleDrawNotFound),
          });
          break;
        case ResultCodes.CANNOT_CREATE_DRAW_REQUEST:
          drawRequestDialog.open({
            title: intl.formatMessage(
              messages.sampleDrawCannotBeRequestedTitle
            ),
            description: intl.formatMessage(
              messages.sampleDrawCannotBeRequestedDesc
            ),
          });
          break;
        case ResultCodes.PROSPERA_EBDD_PASSED:
          setIsProsperaEbddPassed(true);
          break;
      }
    }
  };

  useEffect(() => {
    setIsKitShippingStep(kitShippingNeeded);
  }, [kitShippingNeeded]);

  useEffect(() => {
    if (orderUidPublic && testUid) {
      handleCheckSample(orderUidPublic, testUid);
      if (testType === TestType.SIGNATERA) {
        getRenewalInfo({ orderUidPublic });
      }
    }
  }, [orderUidPublic, testUid]);

  const isLoading = DrawRequestLoading || OrderRenewalLoading;

  return (
    <>
      <SpinnerView isLoading={isLoading} />
      {isProsperaEbddPassed ? (
        <>
          <FlowHeader />
          <OrderInWrongStateScreen />
        </>
      ) : !isPossibleToCreate ? (
        <>{drawRequestDialog.getDialog()}</>
      ) : (
        <>
          <FlowHeader />
          {isKitShippingStep ? (
            <div className="progress-stepper">
              <div className="progress-stepper__header" />
              <section className="progress-stepper__section">
                <SampleDrawKitStep
                  onSubmit={handleKitShippingStepPassed}
                  onBack={handleBackKitStep}
                />
              </section>
            </div>
          ) : (
            <MobilePhlebotomyStepper
              onBackPreferenceStep={handleBackPreferenceStep}
              onBackAppointmentTimeStep={handleBackAppointmentTimeStep}
              onBackKitShippingStep={handleBackKitShippingStep}
              profileAddresses={profileData?.addresses}
              flow={DrawRequestFlow.ACCOUNT}
            />
          )}
          {timingDialog.getDialog()}
          {comboOrderModal.getDialog()}
        </>
      )}
    </>
  );
};

export default CreateMobilePhlebotomy;
